/**
 * 取得に成功したpt取得コードの記憶
 * 成功直後に同じQRを読み直したり、URLのtokenを持ったまま画面を開き直したりしたときに、
 * 取得済みエラーで直前の成功表示を上書きしないよう、このページで成功したコードを来場者セッションごとに覚えます。
 * 端末へは保存せず、ページを読み込み直すと消えます。
 */

import { isPointDistributionManualCode } from '../../../shared/utils/pointClaimQr';
import { POINT_DISTRIBUTION_PREFIX } from '../services/visitorPointDistributionService';

/** このページで取得に成功したコード（来場者セッションとコードの組） */
const claimedPointTokenKeys = new Set<string>();

/**
 * 記憶のキーを作る
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {string} token - 正規化済みのpt取得コード
 * @returns {string} 記憶のキー
 */
const buildClaimedPointTokenKey = (sessionToken: string, token: string): string => `${sessionToken}\n${token}`;

/**
 * 取得に成功したコードを覚える
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {string} token - 正規化済みのpt取得コード
 * @returns {void}
 */
export const rememberClaimedPointToken = (sessionToken: string, token: string): void => {
  if (!sessionToken || !token) {
    return;
  }

  claimedPointTokenKeys.add(buildClaimedPointTokenKey(sessionToken, token));
};

/**
 * このページで取得に成功したコードか
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {string} token - 正規化済みのpt取得コード
 * @returns {boolean} 取得に成功済みならtrue
 */
export const hasClaimedPointToken = (sessionToken: string, token: string): boolean =>
  Boolean(sessionToken && token) && claimedPointTokenKeys.has(buildClaimedPointTokenKey(sessionToken, token));

/**
 * 1人が同じコードで複数回受け取れる配布QR・配布コードか
 * 企画QRとアンケートは1アカウント1回のため、読み直しても受け取れない。
 * @param {string} token - 正規化済みのpt取得コード
 * @returns {boolean} 複数回受け取れる場合はtrue
 */
export const isRepeatablePointClaimToken = (token: string): boolean =>
  token.startsWith(POINT_DISTRIBUTION_PREFIX) || isPointDistributionManualCode(token);

/**
 * 覚えたコードをすべて消す
 * @returns {void}
 */
export const clearClaimedPointTokens = (): void => {
  claimedPointTokenKeys.clear();
};
